import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, formatApiError } from "../lib/api";
import { useI18n } from "../i18n";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const { changeLang } = useI18n();
  const [user, setUser] = useState(null); // null = loading, false = logged out
  const [wallet, setWallet] = useState(null);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("rl_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.lang_set_by && data.lang && data.lang !== localStorage.getItem("rl_lang")) changeLang(data.lang, { silent: true });
      try { localStorage.setItem("rl_user_cache", JSON.stringify(data)); } catch {}
      // silent renewal: customers/agents never see a login screen again unless they log out
      try {
        const { exp, role } = JSON.parse(atob(token.split(".")[1] || "")) || {};
        if (role !== "admin" && exp && exp * 1000 - Date.now() < 300 * 86400000) {
          const r = await api.post("/auth/renew");
          if (r.data?.token) setToken(r.data.token);
        }
      } catch {}
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 403) { setToken(null); setUser(false); try { localStorage.removeItem("rl_user_cache"); } catch {} return; }
      // offline / server hiccup → stay signed in with the cached profile
      try { const c = JSON.parse(localStorage.getItem("rl_user_cache") || "null"); setUser(c || false); } catch { setUser(false); }
    }
  }, [changeLang]);

  const refreshWallet = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet");
      setWallet(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (user) refreshWallet();
  }, [user, refreshWallet]);

  // Realtime-ish wallet: poll every 8s while the tab is visible, refresh on focus/return and on wallet-affecting notifications
  useEffect(() => {
    if (!user) return undefined;
    const tick = () => { if (document.visibilityState === "visible") refreshWallet(); };
    const id = setInterval(tick, 8000);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("rl-wallet-changed", tick);
    return () => { clearInterval(id); window.removeEventListener("focus", tick); document.removeEventListener("visibilitychange", tick); window.removeEventListener("rl-wallet-changed", tick); };
  }, [user, refreshWallet]);

  // Account changes made by admin (party ↔ customer, customer → agent) must show up without a reload: re-read /auth/me on return + every 60s
  useEffect(() => {
    if (!user) return undefined;
    const sync = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data } = await api.get("/auth/me");
        setUser((u) => (u && (u.account_type !== data.account_type || u.role !== data.role || u.party_tier !== data.party_tier || u.is_blocked !== data.is_blocked || u.name !== data.name) ? data : u));
      } catch { /* ignore */ }
    };
    const id = setInterval(sync, 60000);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => { clearInterval(id); window.removeEventListener("focus", sync); document.removeEventListener("visibilitychange", sync); };
  }, [user?.id]); // eslint-disable-line

  const login = async (mobile, password) => {
    const { data } = await api.post("/auth/login", { mobile, password });
    setToken(data.token);
    setUser(data.user);
    syncLang(data.user);
    return data.user;
  };

  // Staff-set language (admin/agent) wins on login; otherwise the device choice is saved to the account.
  const syncLang = (u) => {
    const l = localStorage.getItem("rl_lang");
    if (u?.lang_set_by && u.lang) { if (u.lang !== l) changeLang(u.lang, { silent: true }); return; }
    if (l && l !== u?.lang) api.put("/auth/lang", { lang: l }).catch(() => {});
  };

  const logout = () => {
    setToken(null);
    setUser(false);
    setWallet(null);
    try { localStorage.removeItem("rl_user_cache"); } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, setUser, wallet, refreshWallet, login, logout, loadUser, syncLang }}>
      {children}
    </AuthContext.Provider>
  );
}

export { formatApiError };
